import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.`init`.InitKobwebContext
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.willandsons.components.layouts.MarkdownLayout as _com_willandsons_components_layouts_MarkdownLayout
import com.willandsons.components.layouts.PageLayout as _com_willandsons_components_layouts_PageLayout
import com.willandsons.pages.AppsPage as _com_willandsons_pages_AppsPage
import com.willandsons.pages.ContactPage as _com_willandsons_pages_ContactPage
import com.willandsons.pages.DeleteAccountPage as _com_willandsons_pages_DeleteAccountPage
import com.willandsons.pages.HomePage as _com_willandsons_pages_HomePage
import com.willandsons.pages.LovenearbySafetyPage as _com_willandsons_pages_LovenearbySafetyPage
import com.willandsons.pages.LovenearbyTermsPage as _com_willandsons_pages_LovenearbyTermsPage
import com.willandsons.pages.NotFoundPage as _com_willandsons_pages_NotFoundPage
import com.willandsons.pages.PrivacyPolicyPage as _com_willandsons_pages_PrivacyPolicyPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Willandsons Website"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.willandsons.components.layouts.MarkdownLayout",
            parentLayoutId = "com.willandsons.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_willandsons_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.willandsons.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_willandsons_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.willandsons.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_HomePage()
        }
        ctx.router.register(
            "/404",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initNotFoundPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_NotFoundPage()
        }
        ctx.router.register(
            "/apps",
            layoutId = "com.willandsons.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initAppsPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_AppsPage()
        }
        ctx.router.register(
            "/contact",
            layoutId = "com.willandsons.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initContactPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_ContactPage()
        }
        ctx.router.register(
            "/delete-account",
            layoutId = "com.willandsons.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initDeleteAccountPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_DeleteAccountPage()
        }
        ctx.router.register(
            "/lovenearby-safety",
            layoutId = "com.willandsons.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initLovenearbySafetyPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_LovenearbySafetyPage()
        }
        ctx.router.register(
            "/lovenearby-terms",
            layoutId = "com.willandsons.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initLovenearbyTermsPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_LovenearbyTermsPage()
        }
        ctx.router.register(
            "/privacy-policy",
            layoutId = "com.willandsons.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.pages.initPrivacyPolicyPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_pages_PrivacyPolicyPage()
        }

        com.willandsons.setErrorPage(ctx)
    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.willandsons.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.willandsons.SubheadlineTextStyle)
        ctx.theme.registerStyle("markdown", com.willandsons.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", com.willandsons.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", com.willandsons.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", com.willandsons.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("hero-container", com.willandsons.pages.HeroContainerStyle)
        ctx.theme.registerStyle("home-image", com.willandsons.pages.HomeImageStyle)
        ctx.theme.registerVariant("-circle", com.willandsons.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", com.willandsons.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", com.willandsons.components.sections.SideMenuSlideInAnim)
        com.willandsons.initColorMode(ctx)
        com.willandsons.initStyles(ctx)
        com.willandsons.initSiteStyles(ctx)
        com.willandsons.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.willandsons.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
